import { useUpdatePetitionMutation } from "@codegen/index";
import { UpdatePetitionByIdInput } from "@codegen/schema";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useBeneficiaryWorkflowContext } from "@pages/onboarding/workflows/provider";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

// Schema definition
export const dependentsFormSchemaDetails = z
  .object({
    hasDependents: z.enum(["Yes", "No"], {
      required_error: "Please select Yes or No",
    }),
    dependentsInUS: z.enum(["Yes", "No"], {
      required_error: "Please select Yes or No",
    }),
    dependentsVisaStatus: z.string(),
  })
  .refine(
    (data) => {
      // If hasDependents is "Yes", then dependentsVisaStatus must be filled
      if (data.hasDependents === "Yes") {
        return data.dependentsVisaStatus.trim().length > 0;
      }
      return true;
    },
    {
      message: "Please enter the visa status",
      path: ["dependentsVisaStatus"],
    }
  );

export type DependentsFormType = z.infer<typeof dependentsFormSchemaDetails>;

export const DependentsForm: React.FC<
  OnboardingPageType & {
    dependentsData?: DependentsFormType;
    refetch: () => void;
  }
> = ({
  petition,
  onSubmit,
  onBack,
  dependentsData,
  refetch,
  onSkipForNow,
  exhibit,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const { isReadOnly } = useReadOnlyMode();
  const { refetchPetitions } = useBeneficiaryWorkflowContext();
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm<DependentsFormType>({
    defaultValues: {
      hasDependents: (dependentsData?.hasDependents || "No") as "Yes" | "No",
      dependentsInUS: (dependentsData?.dependentsInUS || "No") as "Yes" | "No",
      dependentsVisaStatus: dependentsData?.dependentsVisaStatus || "",
    },
    resolver: zodResolver(dependentsFormSchemaDetails),
  });

  const { control, setError } = methods;
  const logError = useLogError();
  const [updatePetitionMutation, { loading: saving }] =
    useUpdatePetitionMutation();

  const hasDependents = useWatch({
    control,
    name: "hasDependents",
  });

  const watchedValues = useWatch({
    control,
  }) as DependentsFormType;

  const performMutation = useCallback(
    async (data: DependentsFormType) => {
      // Get existing metadata and merge with new data
      const existingMetadata = petition.metadata
        ? JSON.parse(petition.metadata)
        : {};
      const newMetadata = {
        ...existingMetadata,
        hasDependents: data.hasDependents,
        ...(data.hasDependents === "Yes" && {
          dependentsInUS: data.dependentsInUS,
          dependentsVisaStatus: data.dependentsVisaStatus,
        }),
      };

      const { errors: updatePetitionError } = await updatePetitionMutation({
        variables: {
          input: {
            id: petition.id,
            petitionPatch: {
              metadata: JSON.stringify(newMetadata),
            },
          } as UpdatePetitionByIdInput,
        },
      });

      if (updatePetitionError != null) {
        throw updatePetitionError;
      }

      await refetch();
      await refetchPetitions();
    },
    [
      petition.id,
      petition.metadata,
      refetch,
      refetchPetitions,
      updatePetitionMutation,
    ]
  );

  const handleSave = useCallback(
    async (data: DependentsFormType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await performMutation(data);

          setStatus("success");
        } catch (exception) {
          setStatus("error"); // Show error for auto-save failure
          if (exception instanceof Error) {
            setError("root.serverError", {
              type: exception.name,
              message: exception.message,
            });
          }
          logError(exception, {
            message: "Error auto-saving dependents data",
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, logError, setError]
  );

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    // Only call debouncedSave if the form data is valid
    const parseResult = dependentsFormSchemaDetails.safeParse(watchedValues);
    if (parseResult.success) {
      debouncedSave(parseResult.data);
    }
  }, [watchedValues, debouncedSave]);

  const handleSubmit = useCallback(
    async (data: DependentsFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        setStatus("success");
        onSubmit(data);
      } catch (exception) {
        setStatus("error");
        if (exception instanceof Error) {
          setError("root.serverError", {
            type: exception.name,
            message: exception.message,
          });
        }
        logError(exception, {
          message: "Error submitting dependents data",
        });
      }
    },
    [performMutation, onSubmit, logError, setError]
  );

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={exhibit.name}
        subtitle={
          <>
            Do you have any dependents? (Spouse or children){" "}
            <span className="text-red-500">*</span>
          </>
        }
        progress={moduleProgress}
        onSubmit={
          methods.handleSubmit(handleSubmit) as (
            data?: unknown
          ) => void | Promise<void>
        }
        onBack={onBack}
        onSkipForNow={onSkipForNow}
        exhibit={exhibit}
        name="dependents-form"
        isLoading={saving}
        currentStep={currentStep}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your dependents information. Please try again."
        >
          <OnboardingFormField
            name="hasDependents"
            type="radio"
            required={true}
            radioOptions={[
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
            ]}
            disabled={isReadOnly}
          />

          {hasDependents === "Yes" && (
            <>
              <OnboardingFormField
                name="dependentsInUS"
                label="Are they currently physically present in the United States?"
                type="radio"
                required={true}
                radioOptions={[
                  { value: "Yes", label: "Yes" },
                  { value: "No", label: "No" },
                ]}
                disabled={isReadOnly}
              />

              <OnboardingFormField
                name="dependentsVisaStatus"
                label="What is their current Visa status? (Example H4, H-1B, B1/B2)"
                type="text"
                required={true}
                placeholder="Enter visa status (e.g., H4, H-1B, B1/B2)"
                disabled={isReadOnly}
              />
            </>
          )}
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const DependentsFormController: React.FC<OnboardingPageType> = (
  props
) => {
  // Get existing data from petition metadata
  const dependentsData = React.useMemo(() => {
    if (props.petition?.metadata) {
      const metadata = JSON.parse(props.petition.metadata);
      return {
        hasDependents: metadata.hasDependents,
        dependentsInUS: metadata.dependentsInUS,
        dependentsVisaStatus: metadata.dependentsVisaStatus,
      };
    }
    return undefined;
  }, [props.petition?.metadata]);

  // No need for loading state since data comes from petition prop
  return (
    <DependentsForm
      {...props}
      dependentsData={dependentsData}
      refetch={() => {}} // No need to refetch since we're using petition metadata
    />
  );
};
